import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { Link } from 'gatsby';
import DOMPurify from 'isomorphic-dompurify';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

const ResourcePreview = ({ resource }) => {
  const { summary, title, slug, previewImage, publishedAt, category } = resource;
  return (
    <Link to={`/blog/${slug.current}/`} key={slug.current} tabindex="0">
      <Preview>
        <GatsbyImage image={getImage(previewImage.asset)} alt={previewImage.alt} />
        <div>
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(category.join('&nbsp;&nbsp;|&nbsp;&nbsp;'))
            }}
          />
          <span>{publishedAt}</span>
        </div>
        <h3 dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(summary) }} />
      </Preview>
    </Link>
  );
};

const ResourcePreviews = ({
  resources,
  pathname,
  categories,
  selectedCategory,
  setSelectedCategory
}) => {
  const _categories = categories.reduce((acc, category) => {
    category.forEach((cat) => {
      if (!acc.includes(cat)) {
        acc.push(cat);
      }
    });
    return acc;
  }, []);

  return (
    <Container id="#resources">
      {_categories.length > 1 && (
        <Categories>
          <Category
            type="button"
            tabindex="0"
            onClick={() => setSelectedCategory('All')}
            selected={selectedCategory === 'All'}>
            All
          </Category>
          {_categories.map((category, index) => (
            <Category
              key={index}
              type="button"
              tabindex="0"
              onClick={() => setSelectedCategory(category)}
              selected={selectedCategory === category}>
              {category}
            </Category>
          ))}
        </Categories>
      )}
      <AnimatePresence>
        <Grid key={resources} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}>
          {resources.map(({ node }, index) => (
            <ResourcePreview key={node.title} resource={node} index={index} pathname={pathname} />
          ))}
        </Grid>
      </AnimatePresence>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 3rem;
  a {
    text-decoration: none;
  }
  @media screen and (min-width: 64em) {
    margin-bottom: 6rem;
  }
`;

const Grid = styled(motion.div)`
  display: grid;
  gap: 1.275rem;
  img {
    height: 200px;
  }
  @media screen and (min-width: 48em) {
    img {
      height: 280px;
    }
  }
  @media screen and (min-width: 64em) {
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    img {
      height: 368px;
    }
  }
`;

const Categories = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin: 2rem 0;
  max-width: 1100px;
  @media screen and (min-width: 64em) {
    margin: 0 0 3rem;
  }
`;

const Category = styled.button`
  color: ${({ selected }) => (selected ? 'var(--ax-gold)' : 'var(--ax-grey)')};
  cursor: pointer;
  background: transparent;
  font-size: 16px;
  font-family: 'proxima-nova';
  font-weight: 700;
  text-decoration: underline;
  text-underline-position: under;
  transition: 150ms ease-in-out;
`;

const Preview = styled.div`
  overflow: hidden;
  position: relative;

  span {
    font-size: 0.75rem;
    font-weight: 700;
  }

  span:first-of-type {
    color: var(--ax-gold);
  }

  span:last-of-type {
    color: var(--ax-grey);
  }

  p {
    font-size: 0.75rem;
    color: var(--ax-black);
    font-weight: 400;
    margin-bottom: 0.5em;
  }

  h3 {
    color: var(--ax-black);
    font-family: 'Butler', serif;
    font-size: 1.325rem;
    margin: 0.325em 0;
  }

  div {
    display: flex;
    gap: 1rem;
    margin: 0.5em 0;
  }

  @media screen and (min-width: 48rem) {
    p {
      font-size: 1rem;
    }

    h3 {
      font-size: 1.5rem;
    }
  }
`;

export default ResourcePreviews;
